import React, { useState, useEffect, useRef } from 'react';
import PoDetailModal from './PoDetailModal';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { NewLayoutBar } from './AdminJobDetail';
import { jobName } from '../../utils/ui';
import { summarizeSubJob, HAS_AI_KEY } from '../../services/ai';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { formatPhone } from '../../utils/phone';

// Fix Leaflet default marker icon broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

const PO_STATUS_COLORS = { pending: '#f59e0b', approved: '#16a34a', rejected: '#dc2626', completed: '#6b7280' };
const PO_STATUSES = ['pending', 'approved', 'completed', 'rejected'];

function POStatusBadge({ poId, initial, onUpdate }) {
  const [status, setStatus] = useState(initial || 'pending');
  const [changing, setChanging] = useState(false);
  async function cycle() {
    const next = PO_STATUSES[(PO_STATUSES.indexOf(status) + 1) % PO_STATUSES.length];
    setChanging(true);
    try {
      await apiFetch(`/purchase-orders/${poId}/status`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      setStatus(next);
      onUpdate?.(next);
    } catch {}
    setChanging(false);
  }
  return (
    <button onClick={cycle} disabled={changing} style={{ padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, textTransform: 'capitalize', background: PO_STATUS_COLORS[status] + '22', color: PO_STATUS_COLORS[status] }}>
      {status}
    </button>
  );
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today))     return 'Today';
  if (same(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function AdminNotesByDate({ notes }) {
  const groups = {};
  notes.forEach(n => {
    const key = new Date(n.created_at).toDateString();
    if (!groups[key]) groups[key] = { label: formatDateLabel(n.created_at), notes: [] };
    groups[key].notes.push(n);
  });
  const dateGroups = Object.values(groups);

  return (
    <div style={{ padding: '0 0 8px' }}>
      {dateGroups.map((g, gi) => (
        <AdminDateGroup key={g.label} group={g} defaultOpen={gi === 0} />
      ))}
    </div>
  );
}

function AdminDateGroup({ group, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', padding: '12px 20px', background: 'none', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      >
        <span style={{ fontWeight: 700, fontSize: 14 }}>{group.label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>{group.notes.length} update{group.notes.length !== 1 ? 's' : ''}</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--gray-muted)' }}>
            <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {group.notes.map(n => (
            <div key={n.id} style={{ display: 'flex', gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--orange)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {((n.first_name || '?')[0] + (n.last_name || '?')[0]).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{n.first_name} {n.last_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>
                    {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>{n.note}</p>
                {n.parsed_sub_jobs?.length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {n.parsed_sub_jobs.map(sj => (
                      <span key={sj} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--surface-alt,#f3f4f6)', color: 'var(--gray-muted)', fontWeight: 600 }}>
                        {sj}
                      </span>
                    ))}
                  </div>
                )}
                {n.media?.length > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {n.media.map((url, i) => /\.(mp4|mov|webm|avi)$/i.test(url) ? (
                      <video key={i} src={url} controls style={{ maxWidth: 280, maxHeight: 200, borderRadius: 8, background: '#000' }} />
                    ) : (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt="" style={{ width: 140, height: 105, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ROLE_COLORS = {
  'Admin': '#e87722', 'Office': '#9333ea', 'Project Manager': '#2563eb',
  'Foreman': '#16a34a', 'Worker': '#6b7280',
};

function initials(first, last) {
  return ((first || '?')[0] + (last || '?')[0]).toUpperCase();
}

function TeamPanel({ projectId, apiFetch }) {
  const [members, setMembers]       = useState([]);
  const [allUsers, setAllUsers]     = useState([]);
  const [showAdd, setShowAdd]       = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    loadMembers();
    apiFetch('/users').then(u => setAllUsers(Array.isArray(u) ? u : [])).catch(() => {});
  }, [projectId]);

  async function loadMembers() {
    setLoading(true);
    try {
      const data = await apiFetch(`/projects/${projectId}/members`);
      setMembers(Array.isArray(data) ? data : []);
    } catch {} finally { setLoading(false); }
  }

  async function addMember(userId) {
    await apiFetch(`/projects/${projectId}/members`, { method: 'POST', body: JSON.stringify({ userId }) });
    setShowAdd(false);
    loadMembers();
  }

  async function removeMember(userId) {
    if (!window.confirm('Remove this person from the project?')) return;
    await apiFetch(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' });
    loadMembers();
  }

  async function toggleSubJob(userId, subJobName, currentlyAssigned) {
    const path = `/projects/${projectId}/members/${userId}/sub-jobs`;
    if (currentlyAssigned) {
      await apiFetch(`${path}/${encodeURIComponent(subJobName)}`, { method: 'DELETE' });
    } else {
      await apiFetch(path, { method: 'POST', body: JSON.stringify({ subJobName }) });
    }
    loadMembers();
  }

  const assignedIds = new Set(members.map(m => m.id));
  const unassigned  = allUsers.filter(u => !assignedIds.has(u.id));

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">Team ({members.length})</h2>
        <button className="admin-secondary-btn" onClick={() => setShowAdd(s => !s)}>
          {showAdd ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {showAdd && (
        <div style={{ padding: '0 20px 16px', borderBottom: '1px solid var(--border)' }}>
          <input
            placeholder="Search employees…"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, boxSizing: 'border-box', marginBottom: 8 }}
            onChange={e => {
              const q = e.target.value.toLowerCase();
              e.target._filtered = unassigned.filter(u =>
                `${u.firstName || u.first_name} ${u.lastName || u.last_name}`.toLowerCase().includes(q)
              );
              e.target.dispatchEvent(new Event('_render'));
            }}
            ref={el => {
              if (el && !el._listening) {
                el._listening = true;
                el.addEventListener('_render', () => el.dispatchEvent(new Event('input')));
              }
            }}
            id={`add-member-search-${projectId}`}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
            {unassigned.map(u => (
              <button
                key={u.id}
                onClick={() => addMember(u.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: 'none', background: 'var(--surface-alt, #f9f9f9)', cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: ROLE_COLORS[u.role] || '#6b7280', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  {initials(u.firstName || u.first_name, u.lastName || u.last_name)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{u.firstName || u.first_name} {u.lastName || u.last_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-muted)' }}>{u.role}</div>
                </div>
              </button>
            ))}
            {unassigned.length === 0 && <div style={{ fontSize: 13, color: 'var(--gray-muted)', padding: '8px 0' }}>All employees already assigned.</div>}
          </div>
        </div>
      )}

      {loading && <div className="admin-empty">Loading…</div>}

      {!loading && members.length === 0 && (
        <div className="admin-empty">No team members assigned yet.</div>
      )}

      {!loading && members.length > 0 && (
        <div style={{ padding: '8px 20px 16px' }}>
          {/* Avatar row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
            {members.map(m => (
              <div
                key={m.id}
                title={`${m.first_name} ${m.last_name} — ${m.project_role || m.role}`}
                onClick={() => setExpandedId(id => id === m.id ? null : m.id)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: expandedId === m.id ? '#e87722' : (ROLE_COLORS[m.role] || '#6b7280'),
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700,
                  border: expandedId === m.id ? '3px solid #c4601a' : '3px solid transparent',
                  transition: 'all 0.15s',
                }}>
                  {initials(m.first_name, m.last_name)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-muted)', maxWidth: 52, textAlign: 'center', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.first_name}
                </div>
              </div>
            ))}
          </div>

          {/* Expanded member detail */}
          {expandedId && (() => {
            const m = members.find(x => x.id === expandedId);
            if (!m) return null;
            return (
              <div style={{ background: 'var(--surface-alt, #f7f7f7)', borderRadius: 12, padding: 16, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{m.first_name} {m.last_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-muted)' }}>{m.project_role || m.role}</div>
                  </div>
                  <button
                    onClick={() => removeMember(m.id)}
                    style={{ background: 'none', border: '1px solid #f00', color: '#d00', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                  >Remove</button>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Sub-job assignments
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {SUB_JOB_ORDER.map(sj => {
                    const assigned = m.subJobs?.includes(sj);
                    return (
                      <button
                        key={sj}
                        onClick={() => toggleSubJob(m.id, sj, assigned)}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          background: assigned ? (SUB_JOB_COLORS[sj] || '#6b7280') : 'transparent',
                          color: assigned ? '#fff' : 'var(--gray-muted)',
                          border: `1.5px solid ${assigned ? (SUB_JOB_COLORS[sj] || '#6b7280') : 'var(--border)'}`,
                          transition: 'all 0.15s',
                        }}
                      >{sj}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

const SUB_JOB_ORDER = [
  'Vertical Drilling',
  'Horizontal Drilling',
  'Loops',
  'Shop/Trucking',
  'Power Company',
  'General',
];

const SUB_JOB_COLORS = {
  'Vertical Drilling':   '#e87722',
  'Horizontal Drilling': '#2563eb',
  'Directional Drilling': '#2563eb',
  'Loops':               '#16a34a',
  'Shop/Trucking':       '#9333ea',
  'Power Company':       '#dc2626',
  'General':             '#6b7280',
};

function ProgressBar({ complete, active, total }) {
  if (!total) return <div className="proj-progress-bar empty" style={{ height: 10 }} />;
  const pctComplete = Math.round((complete / total) * 100);
  const pctActive   = Math.round((active   / total) * 100);
  return (
    <div className="proj-progress-bar" style={{ height: 10 }}>
      <div className="proj-progress-fill complete" style={{ width: `${pctComplete}%` }} />
      <div className="proj-progress-fill active"   style={{ width: `${pctActive}%` }} />
    </div>
  );
}

function SubJobCard({ name, counts, tasks, projectName, externalSummary, onSummaryGenerated }) {
  const [expanded, setExpanded]   = useState(false);
  const [summary, setSummary]     = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const { total = 0, complete = 0, active = 0, pending = 0 } = counts;
  const pct   = total ? Math.round((complete / total) * 100) : 0;
  const color = SUB_JOB_COLORS[name] || '#6b7280';

  // Accept externally-triggered summary (e.g. "Summarize All" button)
  useEffect(() => {
    if (externalSummary !== undefined) setSummary(externalSummary);
  }, [externalSummary]);

  async function handleSummarize(e) {
    e.stopPropagation();
    setSummarizing(true);
    try {
      const text = await summarizeSubJob(projectName, name, tasks);
      setSummary(text);
      onSummaryGenerated?.(name, text);
    } catch {
      setSummary('Could not generate summary.');
    } finally {
      setSummarizing(false);
    }
  }

  return (
    <div className="sj-card">
      <div className="sj-card-header" onClick={() => setExpanded(e => !e)}>
        <div className="sj-card-chevron" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="sj-card-dot" style={{ background: color }} />
        <div className="sj-card-info">
          <div className="sj-card-name">{name}</div>
          <div className="sj-card-meta">{total} tasks · {pct}% complete</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {complete > 0 && <span className="sj-badge complete">{complete} done</span>}
          {active   > 0 && <span className="sj-badge active">{active} active</span>}
          {pending  > 0 && <span className="sj-badge pending">{pending} pending</span>}
        </div>
        <div style={{ width: 120, flexShrink: 0 }}>
          <ProgressBar complete={complete} active={active} total={total} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color, width: 40, textAlign: 'right', flexShrink: 0 }}>{pct}%</div>
        {HAS_AI_KEY && !summary && (
          <button
            className="admin-secondary-btn"
            style={{ fontSize: 11, padding: '3px 8px', flexShrink: 0 }}
            onClick={handleSummarize}
            disabled={summarizing}
          >
            {summarizing ? '…' : '✦ Summarize'}
          </button>
        )}
      </div>

      {/* AI summary strip */}
      {summary && (
        <div style={{
          padding: '10px 20px 12px 44px',
          fontSize: 13,
          color: 'var(--gray-muted)',
          borderTop: '1px solid var(--border)',
          lineHeight: 1.55,
          fontStyle: 'italic',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}>
          <span style={{ color, fontStyle: 'normal', fontSize: 14, flexShrink: 0 }}>✦</span>
          <span>{summary}</span>
          <button
            onClick={e => { e.stopPropagation(); setSummary(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-muted)', fontSize: 16, lineHeight: 1, marginLeft: 'auto', flexShrink: 0 }}
            title="Dismiss"
          >×</button>
        </div>
      )}

      {expanded && (
        <div className="sj-card-tasks">
          {tasks.length === 0 ? (
            <div className="proj-empty-sub">No tasks in this category.</div>
          ) : (
            tasks.map(t => (
              <div key={t.id} className="sj-task-row">
                <div className={`sj-task-status-dot ${t.status}`} />
                <div className="sj-task-title">{t.title}</div>
                {t.trello_list_name && (
                  <span className="sj-task-list">{t.trello_list_name}</span>
                )}
                {t.due_date && (
                  <span className="sj-task-due">
                    Due {new Date(String(t.due_date).slice(0,10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function LocationsPanel({ projectId }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [query, setQuery] = useState('');
  const [label, setLabel] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [pending, setPending] = useState(null); // { display_name, lat, lng }
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    apiFetch(`/projects/${projectId}/locations`).then(d => setLocations(Array.isArray(d) ? d : [])).catch(() => {});
  }, [projectId]);

  function onQueryChange(val) {
    setQuery(val);
    setPending(null);
    setSuggestions([]);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 4) return;
    if (inputRef.current) setDropdownRect(inputRef.current.getBoundingClientRect());
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await apiFetch(`/geocode?q=${encodeURIComponent(val)}`);
        if (inputRef.current) setDropdownRect(inputRef.current.getBoundingClientRect());
        setSuggestions(Array.isArray(results) ? results : []);
      } catch {}
      setSearching(false);
    }, 500);
  }

  function selectSuggestion(s) {
    setPending(s);
    setQuery(s.display_name);
    setSuggestions([]);
  }

  async function addLocation() {
    if (!pending) return;
    setSaving(true);
    try {
      const loc = await apiFetch(`/projects/${projectId}/locations`, {
        method: 'POST',
        body: JSON.stringify({ label: label.trim() || null, address: pending.display_name, lat: pending.lat, lng: pending.lng }),
      });
      setLocations(ls => [...ls, loc]);
      setQuery(''); setLabel(''); setPending(null); setSuggestions([]);
    } catch {}
    setSaving(false);
  }

  async function removeLocation(locId) {
    await apiFetch(`/projects/${projectId}/locations/${locId}`, { method: 'DELETE' });
    setLocations(ls => ls.filter(l => l.id !== locId));
  }

  const mappable = locations.filter(l => l.lat && l.lng);
  const center = mappable.length > 0 ? [mappable[0].lat, mappable[0].lng] : [39.5296, -119.8138]; // Reno, NV

  return (
    <div className="admin-card">
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', textAlign: 'left' }}
      >
        <h2 className="admin-card-title" style={{ margin: 0 }}>Locations ({locations.length})</h2>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: 'var(--gray-muted)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && <>
      {/* Map */}
      {mappable.length > 0 && (
        <div style={{ height: 260, margin: '0 0 16px', borderRadius: 0, overflow: 'hidden' }}>
          <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
            {mappable.map(l => (
              <Marker key={l.id} position={[l.lat, l.lng]}>
                <Popup>{l.label || l.address}</Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* Existing locations list */}
      {locations.length > 0 && (
        <div style={{ padding: '0 20px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {locations.map(l => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: 'var(--surface-alt,#f9fafb)', borderRadius: 8 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--orange)', flexShrink: 0, marginTop: 2 }}>
                <path d="M8 1.5C5.5 1.5 3.5 3.5 3.5 6c0 3.5 4.5 8.5 4.5 8.5S12.5 9.5 12.5 6c0-2.5-2-4.5-4.5-4.5Z" stroke="currentColor" strokeWidth="1.3" fill="none"/>
                <circle cx="8" cy="6" r="1.5" fill="currentColor"/>
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                {l.label && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{l.label}</div>}
                <div style={{ fontSize: 12, color: 'var(--gray-muted)', lineHeight: 1.4 }}>{l.address}</div>
                {l.lat && <div style={{ fontSize: 11, color: 'var(--gray-muted)', marginTop: 2 }}>{l.lat.toFixed(5)}, {l.lng.toFixed(5)}</div>}
              </div>
              <button onClick={() => removeLocation(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-muted)', padding: 4, lineHeight: 1 }} title="Remove">✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Add location */}
      <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-muted)' }}>Add Location</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="field-input"
            placeholder="Label (optional, e.g. Main entrance)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            style={{ maxWidth: 200 }}
          />
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              ref={inputRef}
              className="field-input"
              placeholder="Search address…"
              value={query}
              onChange={e => onQueryChange(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            {searching && <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--gray-muted)', pointerEvents: 'none' }}>Searching…</div>}
            {suggestions.length > 0 && dropdownRect && (
              <div style={{ position: 'fixed', left: dropdownRect.left, top: dropdownRect.bottom + 4, width: dropdownRect.width, zIndex: 9999, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 260, overflowY: 'auto' }}>
                {suggestions.map((s, i) => (
                  <button key={i} onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }} style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13, cursor: 'pointer', lineHeight: 1.5 }}>
                    {s.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={addLocation}
            disabled={!pending || saving}
            style={{ padding: '0 18px', borderRadius: 8, border: 'none', background: pending && !saving ? 'var(--orange)' : '#e5e7eb', color: pending && !saving ? '#fff' : '#9ca3af', fontWeight: 700, fontSize: 14, cursor: pending ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', flexShrink: 0 }}
          >{saving ? '…' : 'Add'}</button>
        </div>
        {pending && (
          <div style={{ fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>✓</span> {pending.lat.toFixed(5)}, {pending.lng.toFixed(5)} — ready to add
          </div>
        )}
      </div>
      </>}
    </div>
  );
}

// ── Workflow tab colour map ───────────────────────────────────────────────────
const WORKFLOW_COLORS = {
  vertical_drilling:   { bg: '#1e3a5f', label: 'Vertical Drilling',   icon: '⬇️' },
  horizontal_drilling: { bg: '#1e3a5f', label: 'Directional Drilling', icon: '➡️' },
  loops:               { bg: '#065f46', label: 'Loops',               icon: '🔁' },
  nve:                 { bg: '#7c2d12', label: 'NVE',                 icon: '⚡' },
  usa_digs:            { bg: '#4a1d96', label: 'USA Digs',            icon: '🗺️' },
};

function SectionHeader({ bg, icon, label, open, toggle }) {
  return (
    <div onClick={toggle} style={{ background: bg || '#1e3a5f', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</span>
      </div>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
        <path d="M2 4l4 4 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function WorkflowTab({ subJob, projectId, bid }) {
  const [open, setOpen] = useState(true);
  const [notes, setNotes] = useState(subJob.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const cfg = WORKFLOW_COLORS[subJob.type] || { bg: '#374151', label: subJob.name, icon: '📋' };

  // Pre-fill drilling fields from bid
  const isDrilling = subJob.type === 'vertical_drilling' || subJob.type === 'horizontal_drilling';

  async function save() {
    setSaving(true);
    try {
      await apiFetch(`/projects/${projectId}/sub-jobs/${subJob.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert('Save failed: ' + e.message); }
    setSaving(false);
  }

  const fieldStyle = { border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: '#f9fafb', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' };

  return (
    <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <SectionHeader bg={cfg.bg} icon={cfg.icon} label={cfg.label} open={open} toggle={() => setOpen(v => !v)} />
      {open && (
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {isDrilling && bid && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              {[
                ['Hole Depth (ft)', bid.drill_hole_depth],
                ['Hole Width (in)', bid.drill_hole_width],
                ['Number of Holes', bid.drill_num_holes],
                ['Hole Length (ft)', bid.drill_hole_length],
                ['Soil Condition', bid.drill_soil_condition],
              ].map(([label, val]) => (
                <div key={label}>
                  <span style={labelStyle}>{label}</span>
                  <div style={{ ...fieldStyle, color: '#374151' }}>{val || '—'}</div>
                </div>
              ))}
              {bid.drill_scope_items && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={labelStyle}>Scope Items</span>
                  <div style={{ ...fieldStyle, whiteSpace: 'pre-wrap', minHeight: 60, color: '#374151' }}>{bid.drill_scope_items}</div>
                </div>
              )}
              {bid.drill_hole_schedule && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={labelStyle}>Hole Schedule</span>
                  <div style={{ ...fieldStyle, whiteSpace: 'pre-wrap', minHeight: 60, color: '#374151' }}>{bid.drill_hole_schedule}</div>
                </div>
              )}
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <span style={{ ...labelStyle, color: '#374151' }}>Additional Notes / Work Log</span>
              </div>
            </div>
          )}
          <div>
            {!isDrilling && <span style={labelStyle}>Notes / Work Details</span>}
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={6}
              placeholder={`Enter details for ${cfg.label}…`}
              style={{ ...fieldStyle, background: '#fff', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: cfg.bg, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            {saved && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject]     = useState(null);
  const [subJobs, setSubJobs]     = useState([]);
  const [tasks, setTasks]         = useState([]);
  const [notes, setNotes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [summaries, setSummaries] = useState({});
  const [pos, setPos]             = useState([]);
  const [openPo, setOpenPo]       = useState(null);   // the orange PO number opens the PO card (Ryan, Oct 12 2026)
  const [bid, setBid]               = useState(null);
  const [bidRecipients, setBidRecipients] = useState([]);
  const [bidItems, setBidItems]     = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showJobDesc, setShowJobDesc]       = useState(true);
  const [showRecipients, setShowRecipients] = useState(false);
  const [showRates, setShowRates]           = useState(false);
  const [showScope, setShowScope]           = useState(false);
  const [showLineItems, setShowLineItems]   = useState(false);
  const [showVendorQuotes, setShowVendorQuotes] = useState(false);
  const [showTeam, setShowTeam]             = useState(true);
  const [showPOs, setShowPOs]               = useState(false);
  const [showNotes, setShowNotes]           = useState(false);

  useEffect(() => { load(); }, [id]);

  async function load() {
    setLoading(true); setError(null);
    try {
      const [projData, taskList, savedSummaries, noteList, poList, bidData] = await Promise.all([
        apiFetch(`/projects/${id}`),
        apiFetch(`/projects/${id}/tasks`),
        apiFetch(`/projects/${id}/summaries`),
        apiFetch(`/projects/${id}/notes`),
        apiFetch(`/projects/${id}/purchase-orders`),
        apiFetch(`/projects/${id}/bid`).catch(() => null),
      ]);
      setProject(projData);
      setSubJobs(Array.isArray(projData?.subJobs) ? projData.subJobs : []);
      setBid(bidData);
      if (bidData?.id) {
        apiFetch(`/bids/${bidData.id}/recipients`).then(r => setBidRecipients(Array.isArray(r) ? r : [])).catch(() => {});
        apiFetch(`/bids/${bidData.id}/items`).then(r => setBidItems(Array.isArray(r?.items) ? r.items : [])).catch(() => {});
      }
      setTasks(Array.isArray(taskList) ? taskList : []);
      setNotes(Array.isArray(noteList) ? noteList : []);
      setPos(Array.isArray(poList) ? poList : []);
      if (savedSummaries && typeof savedSummaries === 'object') {
        const flat = {};
        Object.entries(savedSummaries).forEach(([k, v]) => { flat[k] = v.summary; });
        setSummaries(flat);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const [promoting, setPromoting] = useState(false);
  async function promoteProject() {
    if (!window.confirm(`Move "${project?.name}" into New Projects?\n\nIt becomes a live job with the full workflow (add tabs like Submittals, Drilling, etc. after). Bid details stay blank — that's fine.`)) return;
    setPromoting(true);
    try {
      await apiFetch(`/projects/${id}/promote`, { method: 'POST' });
      navigate(`/admin/new-projects/${id}`);
    } catch (e) {
      alert(e?.error || e?.message || 'Could not move this project.');
      setPromoting(false);
    }
  }

  async function deleteProject() {
    try {
      await apiFetch(`/projects/${id}`, { method: 'DELETE' });
      navigate('/admin/new-projects');
    } catch (e) { alert('Delete failed: ' + (e.message || e.error)); }
  }

  async function saveSummary(subJobName, summary) {
    try { await apiFetch(`/projects/${id}/summaries`, { method: 'POST', body: JSON.stringify({ subJobName, summary }) }); } catch {}
  }

  // Tasks still classified for the AI summarize feature
  const SUB_JOB_KEYWORDS = {
    'Vertical Drilling':   ['vertical'],
    'Horizontal Drilling': ['horizontal', 'boring', 'directional'],
    'Loops':               ['loop'],
    'Shop/Trucking':       ['shop', 'truck', 'material', 'warehouse', 'delivery'],
    'Power Company':       ['power company', 'nve', 'utility', 'power co', 'nevadaenergy'],
  };
  function classifyTask(task) {
    const hay = [task.title || '', task.description || '', task.trello_list_name || '',
      ...(task.labels ? JSON.parse(task.labels).map(l => l.name) : [])].join(' ').toLowerCase();
    for (const [name, kws] of Object.entries(SUB_JOB_KEYWORDS)) { if (kws.some(kw => hay.includes(kw))) return name; }
    return 'General';
  }
  const buckets = {};
  for (const t of tasks) {
    const cat = classifyTask(t);
    if (!buckets[cat]) buckets[cat] = { counts: { total: 0, complete: 0, active: 0, pending: 0 }, tasks: [] };
    buckets[cat].counts.total++;
    if (t.status === 'complete') buckets[cat].counts.complete++;
    else if (t.status === 'active') buckets[cat].counts.active++;
    else buckets[cat].counts.pending++;
    buckets[cat].tasks.push(t);
  }
  const trelloSubJobKeys = SUB_JOB_ORDER.filter(k => buckets[k]);

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <AdminLayout>
      {/* This page and /admin/new-projects/:id are two different project pages,
          and the Projects list links here — so the way across has to be on both. */}
      <NewLayoutBar id={id} />
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/admin/new-projects')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-muted)', padding: 0, display: 'flex', alignItems: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div>
            <h1 className="admin-page-title">
              {loading ? 'Loading…' : (project?.project_number ? `#${project.project_number} — ` : '') + (jobName(project) || 'Project')}
            </h1>
            <p className="admin-page-sub">
              {project?.division === 'small' ? 'Work Order' : 'Large Job'}
              {project?.status ? ` · ${project.status}` : ''}
              {project?.start_date ? ` · Started ${fmtDate(project.start_date)}` : ''}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!loading && !bid && (
            <button onClick={promoteProject} disabled={promoting}
              title="Move this project into New Projects (the live workflow)"
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: promoting ? 'default' : 'pointer', opacity: promoting ? 0.6 : 1 }}>
              {promoting ? 'Moving…' : 'Move to New Projects →'}
            </button>
          )}
          {bid && (
            <button className="admin-secondary-btn" onClick={() => navigate(`/admin/bids/${bid.id}`)}>
              View Bid
            </button>
          )}
          <button className="admin-primary-btn" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Delete
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px' }}>
              <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Delete project?</span>
              <button onClick={deleteProject} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Yes</button>
              <button onClick={() => setConfirmDelete(false)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="mock-banner" style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>{error}</div>}
      {loading && <div className="admin-empty">Loading project…</div>}

      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* ── Job Description (from bid) ───────────────────────────── */}
          <div className="admin-card" style={{ overflow: 'hidden' }}>
            <SectionHeader bg="#1e3a5f" icon="📋" label="Job Description" open={showJobDesc} toggle={() => setShowJobDesc(v => !v)} />
            {showJobDesc && (
              <div style={{ padding: 20 }}>
                {bid ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      {[
                        ['Job Name',       bid.job_name],
                        ['Location',       bid.job_location],
                        ['Bid Date',       fmtDate(bid.bid_date)],
                        ['Bid Type',       bid.bid_type === 'Lump Sum' ? 'Unit Price' : bid.bid_type],
                        ['Job Type',       bid.job_type],
                        ['Public/Private', bid.job_sector],
                        ['Labor Rate',     bid.labor_rate ? `$${bid.labor_rate}/hr` : null],
                        ['Profit %',       bid.profit_pct != null ? `${bid.profit_pct}%` : null],
                        ['Overhead Material', bid.overhead_material_pct != null ? `${bid.overhead_material_pct}%` : null],
                        ['Overhead Labor',    bid.overhead_labor_pct != null ? `${bid.overhead_labor_pct}%` : null],
                        ['Sales Tax',         bid.sales_tax_pct != null ? `${bid.sales_tax_pct}%` : null],
                        ['Certified Payroll', bid.certified_payroll ? 'Required' : 'Not Required'],
                      ].filter(([, v]) => v != null && v !== '' && v !== '—').map(([label, value]) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
                          <span style={{ fontSize: 13, color: '#111827', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb' }}>{value}</span>
                        </div>
                      ))}
                    </div>

                    {/* Client */}
                    {bid.client_name && (
                      <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Client / Project Owner</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px 20px' }}>
                          {[
                            ['Company', bid.client_name],
                            ['Contact', bid.client_contact],
                            ['Phone',   formatPhone(bid.client_phone)],
                            ['Email',   bid.client_email],
                            ['Address', [bid.client_address1, bid.client_address2].filter(Boolean).join(', ')],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</div>
                              <div style={{ fontSize: 13, color: '#111827', marginTop: 2 }}>{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Awarded by */}
                    {project?.awarded_by_name && (
                      <div style={{ marginTop: 10, padding: '14px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Awarded By</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px 20px' }}>
                          {[
                            ['Name',  project.awarded_by_name],
                            ['Email', project.awarded_by_email],
                            ['Phone', formatPhone(project.awarded_by_phone)],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</div>
                              <div style={{ fontSize: 13, color: '#111827', marginTop: 2 }}>{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: 12 }}>
                      <button onClick={() => navigate(`/admin/bids/${bid.id}`)}
                        style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Open Full Bid →
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>No bid linked to this project.</div>
                )}
              </div>
            )}
          </div>

          {/* ── Bid Recipients ───────────────────────────────────────── */}
          {bid && (
            <div className="admin-card" style={{ overflow: 'hidden' }}>
              <SectionHeader bg="#1e4d3a" icon="📬" label={`Bid Recipients (${bidRecipients.length})`} open={showRecipients} toggle={() => setShowRecipients(v => !v)} />
              {showRecipients && (
                <div style={{ padding: 20 }}>
                  {bidRecipients.length === 0 ? (
                    <div style={{ color: '#9ca3af', fontSize: 13 }}>No recipients on this bid.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {bidRecipients.map(r => (
                        <div key={r.id} style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px 16px' }}>
                          {[['Company', r.client_name], ['Contact', r.contact_name], ['Email', r.contact_email], ['Phone', formatPhone(r.contact_phone)]].filter(([, v]) => v).map(([label, val]) => (
                            <div key={label}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</div>
                              <div style={{ fontSize: 13, color: '#111827', marginTop: 1 }}>{val}</div>
                            </div>
                          ))}
                          {r.sent_at && <div><div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Sent</div><div style={{ fontSize: 12, color: '#16a34a', marginTop: 1 }}>✓ {fmtDate(r.sent_at)}</div></div>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => navigate(`/admin/bids/${bid.id}`)} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit in Bid →</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Labor Costs & Mark Ups ────────────────────────────────── */}
          {bid && (
            <div className="admin-card" style={{ overflow: 'hidden' }}>
              <SectionHeader bg="#064e3b" icon="💰" label="Labor Costs & Mark Ups" open={showRates} toggle={() => setShowRates(v => !v)} />
              {showRates && (
                <div style={{ padding: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                    {[
                      ['Labor Rate ($/hr)', bid.labor_rate != null ? `$${bid.labor_rate}` : '—'],
                      ['Supervision %',    bid.supervision_pct != null ? `${bid.supervision_pct}%` : '—'],
                      ['Sales Tax %',      bid.sales_tax_pct != null ? `${bid.sales_tax_pct}%` : '—'],
                      ['Overhead – Mat %', bid.overhead_material_pct != null ? `${bid.overhead_material_pct}%` : '—'],
                      ['Overhead – Labor %', bid.overhead_labor_pct != null ? `${bid.overhead_labor_pct}%` : '—'],
                      ['Profit %',         bid.profit_pct != null ? `${bid.profit_pct}%` : '—'],
                    ].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb' }}>{val}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => navigate(`/admin/bids/${bid.id}`)} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit in Bid →</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Scope & Bid Details ───────────────────────────────────── */}
          {bid && (bid.scope || bid.exclusions || bid.bid_notes || bid.drill_hole_depth || bid.drill_scope_items) && (
            <div className="admin-card" style={{ overflow: 'hidden' }}>
              <SectionHeader bg="#4a1d50" icon="📝" label="Scope & Bid Details" open={showScope} toggle={() => setShowScope(v => !v)} />
              {showScope && (
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {(bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling') && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                      {[
                        ['Hole Depth (ft)', bid.drill_hole_depth],
                        ['Hole Width (in)', bid.drill_hole_width],
                        ['# of Holes',     bid.drill_num_holes],
                        ['Hole Length (ft)',bid.drill_hole_length],
                        ['Soil Condition', bid.drill_soil_condition],
                      ].filter(([, v]) => v != null).map(([label, val]) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
                          <span style={{ fontSize: 13, color: '#111827', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb' }}>{val}</span>
                        </div>
                      ))}
                      {bid.drill_scope_items && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Scope Items</div>
                          <div style={{ fontSize: 13, color: '#374151', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb', whiteSpace: 'pre-wrap' }}>{bid.drill_scope_items}</div>
                        </div>
                      )}
                      {bid.drill_hole_schedule && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Hole Schedule</div>
                          <div style={{ fontSize: 13, color: '#374151', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb', whiteSpace: 'pre-wrap' }}>{bid.drill_hole_schedule}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {bid.scope && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Scope of Work</div>
                      <div style={{ fontSize: 13, color: '#374151', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb', whiteSpace: 'pre-wrap' }}>{bid.scope}</div>
                    </div>
                  )}
                  {bid.exclusions && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Exclusions / Additional Comments</div>
                      <div style={{ fontSize: 13, color: '#374151', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb', whiteSpace: 'pre-wrap' }}>{bid.exclusions}</div>
                    </div>
                  )}
                  {bid.bid_notes && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Bid Notes</div>
                      <div style={{ fontSize: 13, color: '#374151', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 7, background: '#f9fafb', whiteSpace: 'pre-wrap' }}>{bid.bid_notes}</div>
                    </div>
                  )}
                  <div>
                    <button onClick={() => navigate(`/admin/bids/${bid.id}`)} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit in Bid →</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Line Items ────────────────────────────────────────────── */}
          {bid && (
            <div className="admin-card" style={{ overflow: 'hidden' }}>
              <SectionHeader bg="#1e3a5f" icon="🧾" label={`Line Items (${bidItems.length})`} open={showLineItems} toggle={() => setShowLineItems(v => !v)} />
              {showLineItems && (
                <div style={{ padding: bidItems.length === 0 ? 20 : 0 }}>
                  {bidItems.length === 0 ? (
                    <div style={{ color: '#9ca3af', fontSize: 13 }}>No line items on this bid.</div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                              {['#', 'Description', 'Qty', 'Unit', 'Mat Cost', 'Labor Hrs', 'Unit Price', 'Total'].map(h => (
                                <th key={h} style={{ padding: '9px 12px', textAlign: h === '#' || h === 'Qty' ? 'center' : 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {bidItems.map((item, idx) => {
                              const fmtMoney = n => n != null ? '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
                              return (
                                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '9px 12px', textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>{idx + 1}</td>
                                  <td style={{ padding: '9px 12px', color: '#111827', fontWeight: 500 }}>{item.description || '—'}</td>
                                  <td style={{ padding: '9px 12px', textAlign: 'center', color: '#374151' }}>{item.quantity}</td>
                                  <td style={{ padding: '9px 12px', color: '#6b7280' }}>{item.unit}</td>
                                  <td style={{ padding: '9px 12px', color: '#374151' }}>{fmtMoney(item.material_cost)}</td>
                                  <td style={{ padding: '9px 12px', color: '#374151' }}>{item.labor_hours}</td>
                                  <td style={{ padding: '9px 12px', color: '#374151', whiteSpace: 'nowrap' }}>{fmtMoney(item.unit_price)}</td>
                                  <td style={{ padding: '9px 12px', fontWeight: 700, color: '#1e3a5f', whiteSpace: 'nowrap' }}>{fmtMoney((item.unit_price || 0) * (item.quantity || 1))}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button onClick={() => navigate(`/admin/bids/${bid.id}`)} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit in Bid →</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Vendor Quotes ─────────────────────────────────────────── */}
          {bid && (() => {
            let quotes = [];
            try { quotes = bid.vendor_quotes ? JSON.parse(bid.vendor_quotes) : []; } catch {}
            if (quotes.length === 0) return null;
            return (
              <div className="admin-card" style={{ overflow: 'hidden' }}>
                <SectionHeader bg="#92400e" icon="🏷️" label={`Vendor Quotes (${quotes.length})`} open={showVendorQuotes} toggle={() => setShowVendorQuotes(v => !v)} />
                {showVendorQuotes && (
                  <div style={{ padding: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {quotes.map((q, i) => (
                        <div key={i} style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fffbeb', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px 16px' }}>
                          {[['Vendor', q.vendor_name || q.vendor], ['Item', q.item_name || q.description], ['Price', q.price != null ? `$${Number(q.price).toLocaleString()}` : null], ['Notes', q.notes]].filter(([, v]) => v).map(([label, val]) => (
                            <div key={label}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</div>
                              <div style={{ fontSize: 13, color: '#111827', marginTop: 1 }}>{val}</div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <button onClick={() => navigate(`/admin/bids/${bid.id}`)} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit in Bid →</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Workflow Tabs (sub_jobs created at award) ────────────── */}
          {subJobs.length > 0 && subJobs.map(sj => (
            <WorkflowTab key={sj.id} subJob={sj} projectId={id} bid={bid} />
          ))}

          {/* ── Team & Locations ─────────────────────────────────────── */}
          <div className="admin-card" style={{ overflow: 'hidden' }}>
            <SectionHeader bg="#374151" icon="👥" label="Team & Locations" open={showTeam} toggle={() => setShowTeam(v => !v)} />
            {showTeam && (
              <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280, borderRight: '1px solid #e5e7eb' }}><TeamPanel projectId={id} apiFetch={apiFetch} /></div>
                <div style={{ flex: 1, minWidth: 280 }}><LocationsPanel projectId={id} /></div>
              </div>
            )}
          </div>

          {/* ── Trello Tasks (if any) ─────────────────────────────────── */}
          {trelloSubJobKeys.length > 0 && trelloSubJobKeys.map(key => (
            <div key={key} className="admin-card" style={{ padding: 0 }}>
              <SubJobCard
                name={key}
                counts={buckets[key].counts}
                tasks={buckets[key].tasks}
                projectName={project?.name || ''}
                externalSummary={summaries[key]}
                onSummaryGenerated={(subJobName, text) => saveSummary(subJobName, text)}
              />
            </div>
          ))}

          {/* ── Purchase Orders ───────────────────────────────────────── */}
          <div className="admin-card" style={{ overflow: 'hidden' }}>
            <SectionHeader bg="#374151" icon="📄" label={`Purchase Orders (${pos.length})`} open={showPOs} toggle={() => setShowPOs(v => !v)} />
            {showPOs && (
              pos.length === 0 ? (
                <div className="admin-empty">No purchase orders yet for this project.</div>
              ) : (
                <div style={{ padding: '0 0 8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid var(--border)' }}>
                        {['PO #', 'Description', 'Submitted By', 'Date', 'Status'].map(h => (
                          <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pos.map(po => (
                        <tr key={po.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 20px', fontWeight: 800, color: 'var(--orange)', fontSize: 15 }}><span onClick={() => setOpenPo(po)} title="Open the PO" style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(232,119,34,0.35)', textUnderlineOffset: 3 }}>#{po.po_number}</span></td>
                          <td style={{ padding: '12px 20px', color: 'var(--text)' }}>
                            <div>{po.description}</div>
                            {po.notes && <div style={{ fontSize: 12, color: 'var(--gray-muted)', marginTop: 3 }}>{po.notes}</div>}
                          </td>
                          <td style={{ padding: '12px 20px', color: 'var(--gray-muted)' }}>{po.first_name} {po.last_name}</td>
                          <td style={{ padding: '12px 20px', color: 'var(--gray-muted)', whiteSpace: 'nowrap' }}>{fmtDate(po.created_at)}</td>
                          <td style={{ padding: '12px 20px' }}>
                            <POStatusBadge poId={po.id} initial={po.status} onUpdate={newStatus => setPos(ps => ps.map(p => p.id === po.id ? { ...p, status: newStatus } : p))} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {openPo && <PoDetailModal po={openPo} onClose={() => setOpenPo(null)} onSaved={() => { setOpenPo(null); load(); }} onDeleted={() => { setOpenPo(null); load(); }} />}
                </div>
              )
            )}
          </div>

          {/* ── Field Updates ─────────────────────────────────────────── */}
          <div className="admin-card" style={{ overflow: 'hidden' }}>
            <SectionHeader bg="#374151" icon="📝" label={`Field Updates (${notes.length})`} open={showNotes} toggle={() => setShowNotes(v => !v)} />
            {showNotes && (
              notes.length === 0
                ? <div className="admin-empty">No field updates yet. Workers submit these from their dashboard.</div>
                : <AdminNotesByDate notes={notes} />
            )}
          </div>

        </div>
      )}
    </AdminLayout>
  );
}
